import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ParkingCardFormPayload } from '../../../../../../core/models/parking-card.model';
import {
  CityParkingVehiclePickerDto,
  IdNameOption,
  ParkingCardRenewalRow,
  ParkingCardUserCandidateDto,
  SaveAdminParkingMemberPayload,
  SubscriptionInfoDto,
  ParkingCardTaiwanSyncStatus,
  ParkingCardTaiwanSyncLogRow,
} from '../../../../../../core/models/parking/parking-card-list.model';
import { PlatformManagedCardType } from '../../../../../../core/models/card-quota/card-type-config.model';
import { ApiParkingCardListService } from '../../../../../../core/services/api/parking/api-parking-card-list.service';
import { ApiCardQuotaService } from '../../../../../../core/services/api/card-quota/api-card-quota.service';
import { MapperParkingCardListService } from '../../../../../../core/mappers/parking/mapper-parking-card-list.service';
import { MapperPlatformCardService } from '../../../../../../core/mappers/card-quota/mapper-platform-card.service';
import { MapperClientQuotaService } from '../../../../../../core/mappers/card-quota/mapper-client-quota.service';
import { ClientQuotaDetail } from '../../../../../../core/models/card-quota/client-quota.model';
import { ApiService } from '../../../../../../core/services/api.service';
import { ClientAccessScopeService } from '../../../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../../../core/services/menu-permission.service';
import { ApiSystemSettingsService } from '../../../../../../core/services/api/system-settings/api-system-settings.service';
import { ClientIInfo } from '../../../../../../core/models/parking.model';
import {
  AccessGroupScope,
  mapAccessGroupScope,
} from '../../../../../../core/models/system-settings/access-group.model';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatNativeDateModule,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  DateAdapter,
  NativeDateAdapter,
} from '@angular/material/core';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';

interface ParkingCardVehicleRow {
  id: number;
  licensePlate: string;
  provinceId: number;
  province: string;
  active: boolean;
  isDeleted: boolean;
  oldLicensePlate?: string;
  cityParkingVehicleId?: number | null;
}

interface ProvinceOption {
  id: number;
  name: string;
}

type ParkingCardField =
  | 'client'
  | 'line'
  | 'name'
  | 'cardType'
  | 'quotaType'
  | 'subscription'
  | 'issueDate'
  | 'expireDate'
  | 'vehicles'
  | 'vehicleRows';

// Custom Date Format: dd/MM/yyyy (ค.ศ. / Gregorian)
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    } as Intl.DateTimeFormatOptions,
    monthYearLabel: {
      year: 'numeric',
      month: 'short',
    } as Intl.DateTimeFormatOptions,
    dateA11yLabel: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: {
      year: 'numeric',
      month: 'long',
    } as Intl.DateTimeFormatOptions,
  },
};

@Component({
  selector: 'app-modal-parking-card-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ModalAdminActivityHistoryComponent,
  ],
  templateUrl: './modal-parking-card-detail.component.html',
  styleUrl: './modal-parking-card-detail.component.css',
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
})
export class ModalParkingCardDetailComponent implements OnChanges {
  @Input() cardId: number | null = null;
  @Input() siteId = 0;
  @Input() userId = 0;
  @Input() initialClientId = 0;
  @Input() mode: 'create' | 'view' | 'edit' | 'quota-create' | 'quota-edit' =
    'create';
  @Input() initialActiveTab:
    | 'userInfo'
    | 'accessInfo'
    | 'vehicles'
    | 'renewals'
    | 'taiwanSync' = 'userInfo';
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private readonly hostRef = inject(ElementRef);
  private readonly flaggedFields = new Set<ParkingCardField>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  private readonly apiParkingCardService = inject(ApiParkingCardListService);
  private readonly apiCardQuotaService = inject(ApiCardQuotaService);
  private readonly apiLegacy = inject(ApiService);
  private readonly apiSettings = inject(ApiSystemSettingsService);
  private readonly clientScope = inject(ClientAccessScopeService);
  private readonly mapper = inject(MapperParkingCardListService);
  private readonly quotaMapper = inject(MapperPlatformCardService);
  private readonly clientQuotaMapper = inject(MapperClientQuotaService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  /** When set, client/subscription pickers are limited to this access group. */
  accessScope: AccessGroupScope | null = null;

  activeTab:
    | 'userInfo'
    | 'accessInfo'
    | 'vehicles'
    | 'review'
    | 'renewals'
    | 'taiwanSync' = 'userInfo';
  hasLine = false;
  payload: ParkingCardFormPayload = this.getDefaultPayload();
  access = {
    subscriptionId: 0,
    rateId: 0,
    rateName: '',
    gateGroupId: 0,
    gateGroupName: '',
    vehicleTypeId: 0,
    cardMode: 1,
    issueDate: '',
    expireDate: '',
    isRenewal: false,
  };

  subscriptions: IdNameOption[] = [];
  cardTypes: IdNameOption[] = [];
  platformCardTypes: PlatformManagedCardType[] = [];
  quotaTypeKey = '';
  quotaAccessLocked = false;
  quotaAccessCardTypeLabel = '';
  quotaAccessSubscriptionLabel = '';
  activeClientQuota: ClientQuotaDetail | null = null;
  isLoadingClientQuota = false;
  provinces: ProvinceOption[] = [];
  renewals: ParkingCardRenewalRow[] = [];
  taiwanSyncStatus: ParkingCardTaiwanSyncStatus | null = null;
  taiwanSyncLogs: ParkingCardTaiwanSyncLogRow[] = [];
  taiwanSyncLogsTotal = 0;
  taiwanSyncLogPage = 1;
  taiwanSyncLogPageSize = 20;
  taiwanSyncLoading = false;
  taiwanResyncing = false;
  expandedTaiwanLogId: number | null = null;
  clients: ClientIInfo[] = [];
  clientId = 0;
  clientSearchText = '';
  isClientDropdownOpen = false;

  isLoading = false;
  isSaving = false;
  isClosing = false;

  verifySearchType: 'phone' | 'lineid' = 'phone';
  verifySearchText = '';
  isVerifying = false;
  userVerified = false;
  verifiedCpUserId: number | null = null;
  verifyCandidates: ParkingCardUserCandidateDto[] = [];
  showCandidateList = false;

  cityVehiclePicker: CityParkingVehiclePickerDto[] = [];
  isVehiclePickerOpen = false;
  isLoadingCityVehicles = false;

  readonly cardModeOptions = [
    { value: 1, label: 'ปกติ' },
    { value: 3, label: 'ถูกระงับ' },
  ];

  // Custom dropdown search texts
  searchCardTypeText = '';
  searchQuotaTypeText = '';
  searchCardModeText = 'ปกติ';
  searchSubscriptionText = '';
  searchVehicleProvinceText = '';
  openVehicleProvinceIndex: number | null = null;
  openDropdown: string | null = null;

  get filteredCardTypes(): IdNameOption[] {
    const term = (this.searchCardTypeText || '').trim().toLowerCase();
    if (!term) return this.cardTypes;
    return this.cardTypes.filter((ct) => ct.name.toLowerCase().includes(term));
  }

  get filteredPlatformCardTypes(): PlatformManagedCardType[] {
    const term = (this.searchQuotaTypeText || '').trim().toLowerCase();
    if (!term) return this.platformCardTypes;
    return this.platformCardTypes.filter((t) =>
      this.quotaTypeLabel(t).toLowerCase().includes(term),
    );
  }

  get filteredSubscriptions(): IdNameOption[] {
    const term = (this.searchSubscriptionText || '').trim().toLowerCase();
    if (!term) return this.subscriptions;
    return this.subscriptions.filter((s) =>
      s.name.toLowerCase().includes(term),
    );
  }

  get filteredProvinces(): ProvinceOption[] {
    const term = (this.searchVehicleProvinceText || '').trim().toLowerCase();
    if (!term) return this.provinces;
    return this.provinces.filter((p) => p.name.toLowerCase().includes(term));
  }

  readonly tabs: {
    id: 'userInfo' | 'accessInfo' | 'vehicles' | 'renewals' | 'taiwanSync';
    label: string;
  }[] = [
    { id: 'userInfo', label: 'ข้อมูลผู้ใช้' },
    { id: 'accessInfo', label: 'ข้อมูลบัตร' },
    { id: 'vehicles', label: 'ทะเบียนรถ' },
    { id: 'renewals', label: 'ต่ออายุสมาชิก' },
    { id: 'taiwanSync', label: 'Parking Sync' },
  ];

  readonly wizardSteps: {
    id: 'userInfo' | 'accessInfo' | 'vehicles' | 'review';
    label: string;
  }[] = [
    { id: 'userInfo', label: 'ข้อมูลผู้ใช้' },
    { id: 'accessInfo', label: 'ข้อมูลบัตร' },
    { id: 'vehicles', label: 'ทะเบียนรถ' },
    { id: 'review', label: 'ตรวจสอบ & บันทึก' },
  ];

  get isWizardMode(): boolean {
    return this.isCreate && !this.isReadOnly;
  }

  get wizardIndex(): number {
    return this.wizardSteps.findIndex((s) => s.id === this.activeTab);
  }

  get isLastWizardStep(): boolean {
    return this.wizardIndex === this.wizardSteps.length - 1;
  }

  goWizardStep(index: number): void {
    const current = this.wizardIndex;
    if (index <= current) {
      this.activeTab = this.wizardSteps[index].id;
      return;
    }
    for (let i = current; i < index; i++) {
      const step = this.wizardSteps[i].id;
      if (step !== 'review' && !this.validateStep(step)) {
        this.activeTab = step;
        return;
      }
    }
    this.activeTab = this.wizardSteps[index].id;
  }

  nextStep(): void {
    this.goWizardStep(this.wizardIndex + 1);
  }

  prevStep(): void {
    if (this.wizardIndex > 0) {
      this.activeTab = this.wizardSteps[this.wizardIndex - 1].id;
    }
  }

  onHasLineChange(checked: boolean): void {
    this.hasLine = checked;
    if (!checked) {
      this.clearVerifiedUser();
      this.verifySearchText = '';
      this.isVehiclePickerOpen = false;
    }
  }

  isInvalid(field: ParkingCardField): boolean {
    return this.flaggedFields.has(field) && !this.isFieldValid(field);
  }

  isVehiclePlateInvalid(v: ParkingCardVehicleRow): boolean {
    return this.flaggedFields.has('vehicleRows') && !v.licensePlate?.trim();
  }

  isVehicleProvinceInvalid(v: ParkingCardVehicleRow): boolean {
    return this.flaggedFields.has('vehicleRows') && !v.provinceId;
  }

  private isFieldValid(field: ParkingCardField): boolean {
    switch (field) {
      case 'client':
        return this.clientId > 0;
      case 'line':
        return !this.hasLine || !!this.payload.uid?.trim();
      case 'name':
        return !!this.payload.name?.trim();
      case 'cardType':
        return Number(this.payload.cardTypeId) > 0;
      case 'quotaType':
        return (
          Number(this.payload.cardTypeId) > 0 &&
          (!!this.selectedPlatformCardType || this.access.subscriptionId > 0)
        );
      case 'subscription':
        return (
          this.access.subscriptionId > 0 &&
          this.access.rateId > 0 &&
          this.access.gateGroupId > 0
        );
      case 'issueDate':
        return !!this.access.issueDate;
      case 'expireDate':
        return !!this.access.expireDate;
      case 'vehicles':
        return this.getActiveVehicles().length === 1;
      case 'vehicleRows':
        return this.getActiveVehicles().every(
          (v) => !!v.licensePlate?.trim() && !!v.provinceId,
        );
    }
  }

  /** Shows the error, marks the field red and moves focus to it. */
  private failField(
    field: ParkingCardField,
    message: string,
    focusKey: string = field,
  ): false {
    this.toast.error(message);
    this.flaggedFields.add(field);
    this.focusField(focusKey);
    return false;
  }

  private focusField(key: string): void {
    setTimeout(() => {
      const host = this.hostRef.nativeElement as HTMLElement;
      const target = host.querySelector<HTMLElement>(`[data-field="${key}"]`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = target.matches('input, textarea, button')
        ? target
        : target.querySelector<HTMLElement>(
            'input:not([readonly]), textarea, button:not([disabled])',
          );
      focusable?.focus({ preventScroll: true });
    });
  }

  get selectedVehicleSummary(): string {
    const v = this.getActiveVehicles()[0];
    if (!v) return '-';
    return [v.licensePlate, v.province].filter(Boolean).join(' · ') || '-';
  }

  get visibleTabs(): {
    id: 'userInfo' | 'accessInfo' | 'vehicles' | 'renewals' | 'taiwanSync';
    label: string;
  }[] {
    let tabs = this.tabs;
    if (this.isQuotaContext) {
      tabs = tabs.filter((t) => t.id !== 'renewals');
    }
    if (!this.cardId || this.isCreate || this.isQuotaCreate) {
      tabs = tabs.filter((t) => t.id !== 'taiwanSync');
    }
    return tabs;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadProvinces();
    if (!this.isQuotaCreate) {
      this.loadCardTypes();
    }

    const afterScope = () => {
      if (this.isQuotaCreate) {
        this.loadPlatformCardTypes();
      }
      if (this.siteId) {
        if (!this.isQuotaCreate) {
          this.loadSubscriptions();
        }
        this.loadClients();
      }
    };

    if (this.siteId && this.userId > 0) {
      this.loadAccessScope(afterScope);
    } else {
      this.accessScope = null;
      afterScope();
    }

    if (this.mode !== 'create' && this.mode !== 'quota-create' && this.cardId) {
      this.getCardData(this.cardId);
    } else {
      this.resetForm();
      if (this.initialClientId > 0 && !this.isQuotaCreate) {
        this.applyClientById(this.initialClientId);
      }
    }
    this.activeTab =
      !this.isWizardMode &&
      this.visibleTabs.some((t) => t.id === this.initialActiveTab)
        ? this.initialActiveTab
        : 'userInfo';
  }

  private loadAccessScope(done?: () => void): void {
    if (!this.siteId || this.userId <= 0) {
      this.accessScope = null;
      done?.();
      return;
    }
    this.apiSettings.getMyAccessGroupScope(this.userId, this.siteId).subscribe({
      next: (res) => {
        this.accessScope = mapAccessGroupScope(res);
        done?.();
      },
      error: () => {
        this.accessScope = null;
        done?.();
      },
    });
  }

  private filterClientsByAccessScope(list: ClientIInfo[]): ClientIInfo[] {
    const allowed = this.accessScope?.clientIds;
    if (allowed == null) return list;
    const set = new Set(allowed);
    return list.filter((c) => set.has(c.id));
  }

  private filterSubscriptionsByAccessScope(
    list: IdNameOption[],
  ): IdNameOption[] {
    if (!this.accessScope?.groupId) return list;
    const set = new Set(this.accessScope.subscriptionIds ?? []);
    return list.filter((s) => set.has(s.id));
  }

  private filterPlatformTypesByAccessScope(
    list: PlatformManagedCardType[],
  ): PlatformManagedCardType[] {
    if (!this.accessScope?.groupId) return list;
    const set = new Set(this.accessScope.subscriptionIds ?? []);
    return list.filter((t) => set.has(t.subscriptionId ?? 0));
  }

  private static readonly CLIENT_OPTIONS_PREVIEW = 20;

  get clientDropdownOptions(): ClientIInfo[] {
    const sorted = [...this.clients].sort((a, b) =>
      a.name.localeCompare(b.name, 'th'),
    );
    const q = this.clientSearchText.trim().toLowerCase();
    if (!q)
      return sorted.slice(
        0,
        ModalParkingCardDetailComponent.CLIENT_OPTIONS_PREVIEW,
      );
    return sorted.filter((c) => c.name.toLowerCase().includes(q));
  }

  get isReadOnly(): boolean {
    if (this.mode === 'view') {
      return true;
    }

    const path = this.router.url;
    if (
      (this.mode === 'create' || this.mode === 'quota-create') &&
      !this.permissionService.canCreate(path)
    ) {
      return true;
    }

    if (
      (this.mode === 'edit' || this.mode === 'quota-edit') &&
      !this.permissionService.canUpdate(path)
    ) {
      return true;
    }

    return false;
  }

  get isClientFieldLocked(): boolean {
    return this.isQuotaContext && this.clientScope.isClientLocked();
  }

  get isClientFieldReadOnly(): boolean {
    return this.isReadOnly || this.isClientFieldLocked || this.isQuotaEdit;
  }

  get requiresClientFirst(): boolean {
    return this.isQuotaCreate && this.clientId <= 0;
  }

  get canProceedQuotaForm(): boolean {
    return (
      !this.isQuotaCreate || (this.clientId > 0 && !this.isLoadingClientQuota)
    );
  }

  get hasQuotaToIssue(): boolean {
    return (
      !this.isQuotaCreate || (this.activeClientQuota?.quotaRemaining ?? 0) > 0
    );
  }

  quotaLevelLabel(level: string): string {
    return this.clientQuotaMapper.quotaLevelLabel(level);
  }

  get isQuotaCreate(): boolean {
    return this.mode === 'quota-create';
  }

  get isQuotaEdit(): boolean {
    return this.mode === 'quota-edit';
  }

  get isQuotaContext(): boolean {
    return this.isQuotaCreate || this.isQuotaEdit;
  }

  get showQuotaCardTypePicker(): boolean {
    return this.isQuotaCreate && !this.quotaAccessLocked && !this.isReadOnly;
  }

  get isCreate(): boolean {
    return this.mode === 'create' || this.mode === 'quota-create';
  }

  get canShowActivityHistory(): boolean {
    return !!this.cardId && this.cardId > 0 && !this.isCreate;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  loadPlatformCardTypes(): void {
    if (!this.siteId) {
      this.platformCardTypes = [];
      return;
    }
    this.apiCardQuotaService
      .getPlatformManagedCardTypes(this.siteId, 'free')
      .subscribe({
        next: (res) => {
          this.platformCardTypes = this.filterPlatformTypesByAccessScope(
            res ?? [],
          );
          if (this.isQuotaCreate && this.clientId > 0) {
            this.resolveQuotaCardAccessFromClient();
            return;
          }
          if (this.platformCardTypes.length === 1 && this.isQuotaCreate) {
            this.quotaTypeKey = this.quotaTypeKeyOf(this.platformCardTypes[0]);
            this.onQuotaTypeChange();
          }
          const matched = this.platformCardTypes.find(
            (t) => this.quotaTypeKeyOf(t) === this.quotaTypeKey,
          );
          if (matched) {
            this.searchQuotaTypeText = this.quotaTypeLabel(matched);
          } else if (this.quotaTypeKey) {
            this.quotaTypeKey = '';
            this.searchQuotaTypeText = '';
            this.onQuotaTypeChange();
          }
        },
        error: () => {
          this.platformCardTypes = [];
          this.toast.error('โหลดประเภทบัตร Platform ล้มเหลว');
        },
      });
  }

  quotaTypeKeyOf(t: PlatformManagedCardType): string {
    return `${t.cardTypeId}:${t.subscriptionId ?? 0}`;
  }

  quotaTypeLabel(t: PlatformManagedCardType): string {
    const sub = t.subscriptionName ? ` / ${t.subscriptionName}` : '';
    return `${t.cardTypeName}${sub}`;
  }

  get selectedPlatformCardType(): PlatformManagedCardType | undefined {
    if (!this.quotaTypeKey) return undefined;
    const [cardTypeId, subscriptionId] = this.quotaTypeKey
      .split(':')
      .map(Number);
    return this.platformCardTypes.find(
      (t) =>
        t.cardTypeId === cardTypeId &&
        (t.subscriptionId ?? 0) === subscriptionId,
    );
  }

  onQuotaTypeChange(): void {
    const type = this.selectedPlatformCardType;
    if (!type) {
      this.payload.cardTypeId = '';
      this.access.subscriptionId = 0;
      this.access.rateId = 0;
      this.access.rateName = '';
      this.access.gateGroupId = 0;
      this.access.gateGroupName = '';
      return;
    }
    this.payload.cardTypeId = String(type.cardTypeId);
    this.access.subscriptionId = type.subscriptionId ?? 0;
    this.onSubscriptionChange();
  }

  loadCardTypes(): void {
    this.apiParkingCardService.getParkingCardTypes().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : [];
        this.cardTypes = list.map((item) => ({
          id: Number(item.id ?? (item as Record<string, unknown>)['Id'] ?? 0),
          name: String(
            item.name ?? (item as Record<string, unknown>)['Name'] ?? '',
          ),
        }));
        const matched = this.cardTypes.find(
          (ct) => String(ct.id) === this.payload.cardTypeId,
        );
        if (matched) this.searchCardTypeText = matched.name;
      },
      error: () => (this.cardTypes = []),
    });
  }

  loadSubscriptions(selectedId = 0): void {
    if (!this.siteId) return;
    this.apiParkingCardService
      .getParkingSubscriptionDropdown(this.siteId, selectedId)
      .subscribe({
        next: (res) => {
          const raw = res as { data?: IdNameOption[]; Data?: IdNameOption[] };
          const mapped = (raw.data ?? raw.Data ?? []).map((x) => ({
            id: Number(x.id ?? 0),
            name: String(x.name ?? ''),
          }));
          this.subscriptions = this.filterSubscriptionsByAccessScope(mapped);
          const matched = this.subscriptions.find(
            (s) => s.id === this.access.subscriptionId,
          );
          if (matched) {
            this.searchSubscriptionText = matched.name;
          } else if (this.access.subscriptionId > 0) {
            // Keep current selection text when viewing out-of-scope card; clear picker id only on create.
            if (this.isCreate) {
              this.access.subscriptionId = 0;
              this.searchSubscriptionText = '';
            }
          }
        },
        error: () => (this.subscriptions = []),
      });
  }

  loadProvinces(): void {
    this.apiParkingCardService.getParkingProvinces().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : [];
        this.provinces = list.map((item) => ({
          id: Number(item.id ?? 0),
          name: String(item.name ?? ''),
        }));
      },
      error: () => {
        this.provinces = [];
        this.toast.error('โหลดรายการจังหวัดล้มเหลว');
      },
    });
  }

  loadClients(): void {
    if (!this.siteId) {
      this.clients = [];
      return;
    }
    if (this.isQuotaContext && this.userId > 0) {
      this.clientScope.load(this.siteId, this.userId).subscribe({
        next: (state) => {
          this.clients = this.filterClientsByAccessScope(state.clients);
          this.applyInitialClientSelection();
        },
        error: () => {
          this.clients = [];
          this.toast.error('โหลดรายการร้านล้มเหลว');
        },
      });
      return;
    }
    this.apiLegacy.GetClientIInfo(this.siteId).subscribe({
      next: (res) => {
        this.clients = this.filterClientsByAccessScope(res ?? []);
        if (this.clientId > 0) {
          const stillAllowed = this.clients.some((c) => c.id === this.clientId);
          if (stillAllowed) {
            this.applyClientById(this.clientId);
          } else if (this.isCreate) {
            this.clientId = 0;
            this.clientSearchText = '';
          } else {
            this.applyClientById(this.clientId);
          }
        }
      },
      error: () => (this.clients = []),
    });
  }

  private applyInitialClientSelection(): void {
    if (this.initialClientId > 0) {
      this.applyClientById(this.initialClientId);
      if (this.isQuotaCreate) {
        this.resolveQuotaCardAccessFromClient();
      }
      return;
    }
    if (this.isClientFieldLocked && this.clients.length === 1) {
      this.selectClient(this.clients[0]);
    }
  }

  openClientDropdown(): void {
    if (this.isClientFieldReadOnly) return;
    this.isClientDropdownOpen = true;
    if (this.clientId > 0 && !this.clientSearchText.trim()) {
      const selected = this.clients.find((c) => c.id === this.clientId);
      if (selected) {
        this.clientSearchText = selected.name;
      }
    }
  }

  closeClientDropdown(): void {
    this.isClientDropdownOpen = false;
    if (this.clientId > 0) {
      const selected = this.clients.find((c) => c.id === this.clientId);
      if (selected) {
        this.clientSearchText = selected.name;
      }
    }
  }

  onClientSearchInput(): void {
    this.isClientDropdownOpen = true;
    if (!this.clientSearchText.trim()) {
      this.clientId = 0;
      return;
    }
    const selected = this.clients.find((c) => c.id === this.clientId);
    if (
      selected &&
      selected.name.toLowerCase() !== this.clientSearchText.trim().toLowerCase()
    ) {
      this.clientId = 0;
    }
  }

  selectClient(client: ClientIInfo): void {
    this.clientId = client.id;
    this.clientSearchText = client.name;
    this.isClientDropdownOpen = false;
    if (this.isQuotaCreate) {
      this.resolveQuotaCardAccessFromClient();
    }
  }

  clearClient(): void {
    if (this.isClientFieldReadOnly) return;
    this.clientId = 0;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
    this.activeClientQuota = null;
    if (this.isQuotaCreate) {
      this.resetQuotaAccessLock();
      this.resetVerifyState();
      this.activeTab = 'userInfo';
    }
  }

  private resetQuotaAccessLock(): void {
    this.quotaAccessLocked = false;
    this.quotaAccessCardTypeLabel = '';
    this.quotaAccessSubscriptionLabel = '';
    this.quotaTypeKey = '';
    this.searchQuotaTypeText = '';
    this.payload.cardTypeId = '';
    this.searchCardTypeText = '';
    this.access.subscriptionId = 0;
    this.searchSubscriptionText = '';
    this.access.rateId = 0;
    this.access.rateName = '';
    this.access.gateGroupId = 0;
    this.access.gateGroupName = '';
    if (this.platformCardTypes.length === 1 && this.isQuotaCreate) {
      this.quotaTypeKey = this.quotaTypeKeyOf(this.platformCardTypes[0]);
      this.onQuotaTypeChange();
    }
  }

  private resolveQuotaCardAccessFromClient(): void {
    this.loadActiveClientQuota(true);
  }

  private loadActiveClientQuota(applyCardAccess: boolean): void {
    if (!this.isQuotaContext || !this.clientId || !this.siteId) {
      this.activeClientQuota = null;
      return;
    }

    this.isLoadingClientQuota = true;
    this.apiCardQuotaService
      .getClientQuotasForTable(
        this.siteId,
        1,
        20,
        this.clientId,
        null,
        'active',
      )
      .subscribe({
        next: (res) => {
          const mapped = this.clientQuotaMapper.mapTable(res);
          const pick =
            mapped.items.find((q) => q.quotaRemaining > 0) ??
            mapped.items.find((q) => q.quotaLevel === 'sub') ??
            mapped.items[0];
          if (!pick) {
            this.isLoadingClientQuota = false;
            this.activeClientQuota = null;
            if (applyCardAccess) {
              this.resetQuotaAccessLock();
              this.toast.error('ไม่พบโควต้า active สำหรับร้านนี้');
            }
            return;
          }
          this.apiCardQuotaService.getClientQuotaById(pick.id).subscribe({
            next: (detail) => {
              this.activeClientQuota = detail;
              this.isLoadingClientQuota = false;
              if (applyCardAccess) {
                this.applyQuotaAccessFromDetail(detail);
              }
            },
            error: () => {
              this.isLoadingClientQuota = false;
              this.activeClientQuota = null;
              if (applyCardAccess) {
                this.tryLockSinglePlatformCardType();
              }
            },
          });
        },
        error: () => {
          this.isLoadingClientQuota = false;
          this.activeClientQuota = null;
          if (applyCardAccess) {
            this.tryLockSinglePlatformCardType();
          }
        },
      });
  }

  private applyQuotaAccessFromDetail(detail: ClientQuotaDetail): void {
    if (!detail.cardTypeId || !detail.subscriptionId) {
      this.tryLockSinglePlatformCardType();
      return;
    }

    const matching = this.platformCardTypes.find(
      (t) =>
        t.cardTypeId === detail.cardTypeId &&
        (t.subscriptionId ?? 0) === detail.subscriptionId,
    );

    if (matching) {
      this.quotaTypeKey = this.quotaTypeKeyOf(matching);
      this.searchQuotaTypeText = this.quotaTypeLabel(matching);
      this.onQuotaTypeChange();
    } else {
      this.payload.cardTypeId = String(detail.cardTypeId);
      const matchedCard = this.cardTypes.find(
        (ct) => String(ct.id) === this.payload.cardTypeId,
      );
      this.searchCardTypeText = matchedCard ? matchedCard.name : '';
      this.access.subscriptionId = detail.subscriptionId;
      const matchedSub = this.subscriptions.find(
        (s) => s.id === this.access.subscriptionId,
      );
      this.searchSubscriptionText = matchedSub ? matchedSub.name : '';
      this.onSubscriptionChange();
    }

    this.quotaAccessLocked = true;
    this.quotaAccessCardTypeLabel =
      detail.cardTypeName || `ประเภท ID ${detail.cardTypeId}`;
    this.quotaAccessSubscriptionLabel =
      detail.subscriptionName || `Subscription ID ${detail.subscriptionId}`;
  }

  private tryLockSinglePlatformCardType(): void {
    this.quotaAccessLocked = false;
    this.quotaAccessCardTypeLabel = '';
    this.quotaAccessSubscriptionLabel = '';
    if (this.platformCardTypes.length === 1) {
      const only = this.platformCardTypes[0];
      this.quotaTypeKey = this.quotaTypeKeyOf(only);
      this.searchQuotaTypeText = this.quotaTypeLabel(only);
      this.onQuotaTypeChange();
      this.quotaAccessLocked = true;
      this.quotaAccessCardTypeLabel = only.cardTypeName;
      this.quotaAccessSubscriptionLabel = only.subscriptionName || '';
    }
  }

  private applyClientById(id: number): void {
    const client = this.clients.find((c) => c.id === id);
    if (client) {
      this.clientId = client.id;
      this.clientSearchText = client.name;
    } else {
      this.clientId = id;
    }
  }

  resetForm(): void {
    const now = new Date();
    const today = this.toDateInput(now);
    const endOfMonth = this.toDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    this.payload = this.getDefaultPayload();
    this.payload.siteId = String(this.siteId || '');
    this.access = {
      subscriptionId: 0,
      rateId: 0,
      rateName: '',
      gateGroupId: 0,
      gateGroupName: '',
      vehicleTypeId: 0,
      cardMode: 1,
      issueDate: today,
      expireDate: endOfMonth,
      isRenewal: false,
    };
    this.searchCardTypeText = '';
    this.searchQuotaTypeText = '';
    this.searchCardModeText = 'ปกติ';
    this.searchSubscriptionText = '';
    this.searchVehicleProvinceText = '';
    this.openVehicleProvinceIndex = null;
    this.openDropdown = null;
    this.renewals = [];
    this.hasLine = false;
    this.flaggedFields.clear();
    this.clientId = 0;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
    this.quotaTypeKey = '';
    this.quotaAccessLocked = false;
    this.quotaAccessCardTypeLabel = '';
    this.quotaAccessSubscriptionLabel = '';
    this.activeClientQuota = null;
    this.isLoadingClientQuota = false;
    this.resetVerifyState();
  }

  resetVerifyState(): void {
    this.verifySearchType = 'phone';
    this.verifySearchText = '';
    this.userVerified = false;
    this.verifiedCpUserId = null;
    this.verifyCandidates = [];
    this.showCandidateList = false;
    this.cityVehiclePicker = [];
  }

  getCardData(id: number): void {
    if (!this.siteId || !this.userId) return;

    this.isLoading = true;
    this.apiParkingCardService
      .getParkingCardById(id, this.siteId, this.userId)
      .subscribe({
        next: (res) => {
          const data = this.mapper.mapDetailFromDto(res);
          this.payload = {
            ...this.getDefaultPayload(),
            cardCode: data.cardCode,
            cardName: data.cardName,
            name: data.name,
            address: data.address ?? '',
            phone: data.telephone ?? '',
            uid: data.uid ?? '',
            lineId: data.uid ?? '',
            cardTypeId: String(data.cardTypeId),
            siteId: String(data.siteId),
            vehicles: data.vehicles.map((v) => ({
              id: v.vehicleId,
              licensePlate: v.plateNumber,
              provinceId: v.provinceId,
              province: v.province,
              active: v.active,
              isDeleted: false,
              oldLicensePlate: v.plateNumber,
              cityParkingVehicleId: v.cityParkingVehicleId,
            })),
          };

          this.access = {
            subscriptionId: data.subscriptionId,
            rateId: data.rateId,
            rateName: '',
            gateGroupId: data.gateGroupId,
            gateGroupName: '',
            vehicleTypeId: 0,
            cardMode: data.cardMode || 1,
            issueDate: this.toDateInput(data.issueDate),
            expireDate: this.toDateInput(data.expireDate),
            isRenewal: false,
          };

          if (data.subscriptionId) {
            this.onSubscriptionChange();
          }

          // Initialize custom dropdown search texts
          const matchedCardType = this.cardTypes.find(
            (ct) => String(ct.id) === this.payload.cardTypeId,
          );
          this.searchCardTypeText = matchedCardType ? matchedCardType.name : '';

          const matchedSub = this.subscriptions.find(
            (s) => s.id === this.access.subscriptionId,
          );
          this.searchSubscriptionText = matchedSub ? matchedSub.name : '';

          this.searchCardModeText =
            this.access.cardMode === 3 ? 'ถูกระงับ' : 'ปกติ';

          this.hasLine = !!data.uid;
          if (data.uid) {
            this.userVerified = true;
            this.verifiedCpUserId = data.cpUserId ?? null;
          }

          if (data.clientId) {
            this.applyClientById(data.clientId);
            if (
              !this.clients.some((c) => c.id === data.clientId) &&
              data.clientName
            ) {
              this.clientSearchText = data.clientName;
            }
          }

          if (this.isQuotaContext && data.subscriptionId) {
            this.quotaAccessLocked = true;
            this.quotaAccessSubscriptionLabel = `Subscription ID ${data.subscriptionId}`;
            if (data.cardTypeId) {
              this.quotaAccessCardTypeLabel =
                data.cardTypeName || `ประเภท ID ${data.cardTypeId}`;
            }
          }

          if (this.isQuotaEdit && data.clientId) {
            this.loadActiveClientQuota(false);
          }

          if (!this.isQuotaCreate) {
            this.loadSubscriptions(data.subscriptionId);
          }
          this.loadRenewals(id);
          this.isLoading = false;
        },
        error: () => {
          this.toast.error('ไม่สามารถโหลดข้อมูลบัตรได้');
          this.isLoading = false;
        },
      });
  }

  loadRenewals(cardId: number): void {
    if (!this.siteId || !this.userId) return;
    this.apiParkingCardService
      .getParkingCardRenewals(cardId, this.siteId, this.userId)
      .subscribe({
        next: (rows) => {
          this.renewals = (rows ?? []).map((r) => ({
            id: Number(r.id ?? 0),
            cardId: Number(r.cardId ?? 0),
            startDate: r.startDate != null ? String(r.startDate) : null,
            endDate: r.endDate != null ? String(r.endDate) : null,
            financeId: r.financeId != null ? Number(r.financeId) : null,
            total: r.total != null ? Number(r.total) : null,
            paidDate: r.paidDate != null ? String(r.paidDate) : null,
            siteId: r.siteId != null ? Number(r.siteId) : null,
            createDate: r.createDate != null ? String(r.createDate) : null,
            updateDate: r.updateDate != null ? String(r.updateDate) : null,
          }));
        },
        error: () => (this.renewals = []),
      });
  }

  onSubscriptionChange(): void {
    const subId = this.access.subscriptionId;
    if (!subId) {
      this.access.rateId = 0;
      this.access.rateName = '';
      this.access.gateGroupId = 0;
      this.access.gateGroupName = '';
      this.access.vehicleTypeId = 0;
      return;
    }

    this.apiParkingCardService.getSubscriptionInfoById(subId).subscribe({
      next: (info) => {
        const raw = info as SubscriptionInfoDto & Record<string, unknown>;
        this.access.rateId = Number(raw.rateId ?? raw['RateId'] ?? 0);
        this.access.rateName = String(raw.rateName ?? raw['RateName'] ?? '');
        this.access.gateGroupId = Number(
          raw.gateGroupId ?? raw['GateGroupId'] ?? 0,
        );
        this.access.gateGroupName = String(
          raw.gateGroupName ?? raw['GateGroupName'] ?? '',
        );
        this.access.vehicleTypeId = Number(
          raw.vehicleTypeId ?? raw['VehicleTypeId'] ?? 0,
        );
      },
      error: () => {
        this.access.rateId = 0;
        this.access.rateName = '';
        this.access.gateGroupId = 0;
        this.access.gateGroupName = '';
        this.toast.error('โหลดข้อมูล Subscription ไม่สำเร็จ');
      },
    });
  }

  verifyUser(): void {
    const text = this.verifySearchText.trim();
    if (!text) {
      this.toast.error('กรุณากรอกข้อมูลเพื่อตรวจสอบ');
      return;
    }
    if (!this.siteId || !this.userId) {
      this.toast.error('ไม่พบ Site ที่เลือก');
      return;
    }

    this.isVerifying = true;
    this.apiParkingCardService
      .verifyParkingCardUser(
        this.verifySearchType,
        text,
        this.siteId,
        this.userId,
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapVerifyUser(res);
          this.verifyCandidates = mapped.candidates;

          if (mapped.candidates.length === 0) {
            this.toast.error('ไม่พบผู้ใช้ที่ตรงกับข้อมูลที่ค้นหา');
            this.userVerified = false;
            this.showCandidateList = false;
          } else if (mapped.requiresSelection) {
            this.showCandidateList = true;
            this.userVerified = false;
            this.toast.info('พบผู้ใช้หลายรายการ กรุณาเลือก');
          } else if (mapped.user) {
            this.applyVerifiedUser(mapped.user);
          }

          this.isVerifying = false;
        },
        error: () => {
          this.toast.error('ตรวจสอบผู้ใช้ไม่สำเร็จ');
          this.isVerifying = false;
        },
      });
  }

  selectVerifiedCandidate(candidate: ParkingCardUserCandidateDto): void {
    this.applyVerifiedUser(candidate);
    this.showCandidateList = false;
  }

  private applyVerifiedUser(user: ParkingCardUserCandidateDto): void {
    this.payload.uid = user.lineId;
    this.payload.lineId = user.lineId;
    this.verifiedCpUserId = user.cpUserId ?? null;
    if (!this.payload.name?.trim()) {
      this.payload.name = user.fullName;
    }
    if (!this.payload.phone?.trim()) {
      this.payload.phone = user.phone;
    }
    this.userVerified = true;
    this.toast.success('ยืนยันตัวตนสำเร็จ');
  }

  clearVerifiedUser(): void {
    this.payload.uid = '';
    this.payload.lineId = '';
    this.verifiedCpUserId = null;
    this.userVerified = false;
    this.verifyCandidates = [];
    this.showCandidateList = false;
    this.cityVehiclePicker = [];
  }

  /**
   * Clears the Line ID display on UI while keeping the original uid in payload for API.
   */
  cancelLineId(): void {
    this.payload.lineId = '';
  }

  openCityVehiclePicker(): void {
    if (!this.userVerified || !this.payload.uid) {
      this.toast.error('กรุณาตรวจสอบผู้ใช้ก่อนเลือกทะเบียน');
      return;
    }

    this.isLoadingCityVehicles = true;
    this.isVehiclePickerOpen = true;
    this.apiParkingCardService
      .getCityParkingVehiclesByLineIdForCard(
        this.payload.uid,
        this.siteId,
        this.userId,
      )
      .subscribe({
        next: (res) => {
          this.cityVehiclePicker = (res ?? []).map((item) => ({
            vehicleId: Number(item.vehicleId ?? 0),
            plateNumber: String(item.plateNumber ?? ''),
            provinceId: Number(item.provinceId ?? 0),
            parkingProvinceId: Number(item.parkingProvinceId ?? 0),
            provinceName: String(item.provinceName ?? ''),
            vehicleTypeName: String(item.vehicleTypeName ?? ''),
          }));
          this.isLoadingCityVehicles = false;
        },
        error: () => {
          this.cityVehiclePicker = [];
          this.isLoadingCityVehicles = false;
          this.toast.error('โหลดทะเบียนจาก City Parking ไม่สำเร็จ');
        },
      });
  }

  addVehicleFromPicker(item: CityParkingVehiclePickerDto): void {
    const parkingProvinceId = this.resolveParkingProvinceId(
      item.parkingProvinceId,
      item.provinceName,
    );
    if (!parkingProvinceId) {
      this.toast.error('ไม่พบจังหวัดในข้อมูล parking_province');
      return;
    }

    if (this.getActiveVehicles().length >= 1) {
      this.toast.error('สามารถเพิ่มทะเบียนรถได้สูงสุด 1 คันเท่านั้น');
      return;
    }

    const exists = this.getActiveVehicles().some(
      (v) =>
        v.licensePlate === item.plateNumber &&
        v.provinceId === parkingProvinceId,
    );
    if (exists) {
      this.toast.info('ทะเบียนนี้มีอยู่แล้ว');
      return;
    }

    const provinceName =
      item.provinceName ||
      this.provinces.find((p) => p.id === parkingProvinceId)?.name ||
      '';

    this.payload.vehicles.push({
      id: 0,
      licensePlate: item.plateNumber,
      provinceId: parkingProvinceId,
      province: provinceName,
      active: true,
      isDeleted: false,
      cityParkingVehicleId: item.vehicleId,
    });
    this.isVehiclePickerOpen = false;
  }

  private resolveParkingProvinceId(
    parkingProvinceId?: number | null,
    provinceName?: string | null,
  ): number {
    if (parkingProvinceId && parkingProvinceId > 0) {
      return parkingProvinceId;
    }

    const normalized = provinceName?.trim();
    if (!normalized) {
      return 0;
    }

    const exact = this.provinces.find((p) => p.name === normalized);
    if (exact) {
      return exact.id;
    }

    const lower = normalized.toLowerCase();
    const fuzzy = this.provinces.find((p) => p.name.toLowerCase() === lower);
    return fuzzy?.id ?? 0;
  }

  onProvinceChange(vehicle: ParkingCardVehicleRow): void {
    const p = this.provinces.find((x) => x.id === vehicle.provinceId);
    vehicle.province = p?.name ?? '';
  }

  getDefaultPayload(): ParkingCardFormPayload {
    return {
      cardCode: '',
      cardName: '',
      name: '',
      idCard: '',
      lineId: '',
      cardBackId: '',
      cardTypeId: '',
      uid: '',
      address: '',
      phone: '',
      cardStatus: 'ปกติ',
      roomOrUser: '',
      regularCardType: '',
      tenantCompany: '',
      baseRate: '',
      parkingCardType: '',
      paymentMethod: 'เงินสด',
      servicePointGroup: '',
      timeRange: 0,
      antiPassback: 0,
      siteId: '',
      accessType: '',
      vehicles: [],
      renewals: [],
    };
  }

  getActiveVehicles(): ParkingCardVehicleRow[] {
    return (this.payload.vehicles as ParkingCardVehicleRow[]).filter(
      (v) => !v.isDeleted,
    );
  }

  setTab(
    tab: 'userInfo' | 'accessInfo' | 'vehicles' | 'renewals' | 'taiwanSync',
  ): void {
    if (this.isQuotaCreate && this.clientId <= 0 && tab !== 'userInfo') {
      this.toast.error('กรุณาเลือกร้านก่อน');
      return;
    }
    this.activeTab = tab;
    if (tab === 'renewals' && this.cardId) {
      this.loadRenewals(this.cardId);
    }
    if (tab === 'taiwanSync' && this.cardId) {
      this.loadTaiwanSync();
    }
  }

  taiwanSyncStatusLabel(status: string | null | undefined): string {
    switch ((status || '').toLowerCase()) {
      case 'success':
        return 'สำเร็จ';
      case 'failed':
        return 'ล้มเหลว';
      case 'pending':
        return 'กำลังส่ง';
      case 'skipped':
        return 'ข้าม';
      case 'not_required':
        return 'ไม่ต้องส่ง';
      default:
        return status ? status : 'ยังไม่เคยส่ง';
    }
  }

  /** ยังไม่เคยมี sync สำเร็จ/attempt — ใช้ปุ่มส่งครั้งแรก */
  get isFirstTaiwanSend(): boolean {
    const st = this.taiwanSyncStatus;
    if (!st) return true;
    if (st.taiwanLastSyncAt || st.taiwanId || st.allocatedCardNo) return false;
    if (this.taiwanSyncLogsTotal > 0) return false;
    const s = (st.taiwanSyncStatus || '').toLowerCase();
    return !s || s === 'not_required';
  }

  get taiwanSendButtonLabel(): string {
    if (this.taiwanResyncing) {
      return this.isFirstTaiwanSend ? 'กำลังส่ง...' : 'กำลังส่งอีกครั้ง...';
    }
    return this.isFirstTaiwanSend ? 'ส่งไป Local' : 'ส่ง Sync อีกครั้ง';
  }

  get taiwanCannotSendReason(): string | null {
    const st = this.taiwanSyncStatus;
    if (!st) return null;
    if (st.canResync) return null;
    if (!st.taiwanSite) return 'ไซต์นี้ไม่ได้เปิด taiwan_site — ส่งไม่ได้';
    if (!st.hasTaiwanUrl) return 'ไซต์นี้ยังไม่มี taiwan_url';
    if (!st.cardCode) return 'บัตรยังไม่มี card_code';
    return 'ไม่สามารถส่ง Taiwan ได้ในขณะนี้';
  }

  taiwanSyncStatusClass(status: string | null | undefined): string {
    switch ((status || '').toLowerCase()) {
      case 'success':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'failed':
        return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      case 'pending':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'skipped':
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
      case 'not_required':
        return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
      default:
        return 'bg-slate-100 text-slate-500';
    }
  }

  loadTaiwanSync(): void {
    if (!this.cardId) return;
    this.taiwanSyncLoading = true;
    this.apiParkingCardService
      .getParkingCardTaiwanSyncStatus(this.cardId)
      .subscribe({
        next: (res) => {
          this.taiwanSyncStatus = this.mapper.mapTaiwanSyncStatus(res);
          this.loadTaiwanSyncLogs();
        },
        error: () => {
          this.taiwanSyncStatus = null;
          this.taiwanSyncLoading = false;
          this.toast.error('โหลดสถานะ Taiwan Sync ไม่สำเร็จ');
        },
      });
  }

  loadTaiwanSyncLogs(): void {
    if (!this.cardId) return;
    this.taiwanSyncLoading = true;
    this.apiParkingCardService
      .getParkingCardTaiwanSyncLog(
        this.cardId,
        this.taiwanSyncLogPage,
        this.taiwanSyncLogPageSize,
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTaiwanSyncLogTable(res);
          this.taiwanSyncLogs = mapped.data;
          this.taiwanSyncLogsTotal = mapped.total;
          this.taiwanSyncLoading = false;
        },
        error: () => {
          this.taiwanSyncLogs = [];
          this.taiwanSyncLogsTotal = 0;
          this.taiwanSyncLoading = false;
          this.toast.error('โหลดประวัติ Taiwan Sync ไม่สำเร็จ');
        },
      });
  }

  taiwanSyncLogPrev(): void {
    if (this.taiwanSyncLogPage <= 1) return;
    this.taiwanSyncLogPage -= 1;
    this.loadTaiwanSyncLogs();
  }

  taiwanSyncLogNext(): void {
    const maxPage = Math.max(
      1,
      Math.ceil(this.taiwanSyncLogsTotal / this.taiwanSyncLogPageSize),
    );
    if (this.taiwanSyncLogPage >= maxPage) return;
    this.taiwanSyncLogPage += 1;
    this.loadTaiwanSyncLogs();
  }

  toggleTaiwanLogPayload(logId: number): void {
    this.expandedTaiwanLogId =
      this.expandedTaiwanLogId === logId ? null : logId;
  }

  resyncTaiwan(): void {
    const status = this.taiwanSyncStatus;
    if (!status?.canResync || !status.cardCode || !this.cardId) {
      this.toast.error('ไม่สามารถส่ง Taiwan ได้ในขณะนี้');
      return;
    }
    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์');
      return;
    }
    if (!this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }

    this.taiwanResyncing = true;
    this.apiParkingCardService
      .resyncParkingCardTaiwan({
        userId: this.userId,
        menuId,
        siteId: status.siteId || this.siteId,
        cardCode: status.cardCode,
      })
      .subscribe({
        next: (res) => {
          const mapped = res as {
            isSuccess?: boolean;
            IsSuccess?: boolean;
            message?: string;
            Message?: string;
          };
          const ok = !!(mapped.isSuccess ?? mapped.IsSuccess);
          if (ok) {
            this.toast.success(
              mapped.message || mapped.Message || 'ส่ง Taiwan สำเร็จ',
            );
            this.taiwanSyncLogPage = 1;
            this.loadTaiwanSync();
          } else {
            this.toast.error(
              mapped.message || mapped.Message || 'ส่ง Taiwan ไม่สำเร็จ',
            );
            this.loadTaiwanSync();
          }
          this.taiwanResyncing = false;
        },
        error: (err) => {
          this.taiwanResyncing = false;
          this.toast.error(err?.error?.message || 'ส่ง Taiwan ไม่สำเร็จ');
        },
      });
  }

  addVehicleLine(): void {
    if (this.getActiveVehicles().length >= 1) {
      this.toast.error('สามารถเพิ่มทะเบียนรถได้สูงสุด 1 คันเท่านั้น');
      return;
    }

    this.payload.vehicles.push({
      id: 0,
      licensePlate: '',
      provinceId: 0,
      province: '',
      active: true,
      isDeleted: false,
    });
  }

  removeVehicle(index: number): void {
    const vehicles = this.payload.vehicles as ParkingCardVehicleRow[];
    const item = vehicles[index];
    if (!item) return;

    if (!item.isDeleted && this.getActiveVehicles().length <= 1) {
      this.toast.error('ต้องเหลือทะเบียนรถอย่างน้อย 1 คัน');
      return;
    }

    if (item.id > 0) {
      item.isDeleted = true;
    } else {
      vehicles.splice(index, 1);
    }
  }

  closeModal(): void {
    this.isClosing = true;
    setTimeout(() => this.close.emit(), 250);
  }

  private onSaveSuccess(): void {
    this.saved.emit();
    this.closeModal();
  }

  save(): void {
    if (this.isReadOnly) return;
    if (!this.validatePayload()) return;

    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์เมนู');
      return;
    }

    const path = this.router.url;
    if (this.isQuotaCreate || this.isCreate) {
      if (!this.permissionService.canCreate(path)) {
        this.toast.error('ไม่มีสิทธิ์สร้าง');
        return;
      }
    } else if (!this.permissionService.canUpdate(path)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }

    const savePayload: SaveAdminParkingMemberPayload = {
      menuId,
      userId: this.userId,
      siteId: this.siteId,
      cardId: this.isQuotaCreate ? null : this.cardId,
      cardCode: this.isCreate ? '' : (this.payload.cardCode ?? ''),
      cardName: this.isCreate
        ? String(this.payload['cardName'] ?? '') || this.payload.name
        : String(this.payload['cardName'] ?? '') || this.payload.cardCode,
      name: this.payload.name,
      address: this.payload.address || null,
      telephone: this.payload.phone || null,
      uid: this.payload.uid,
      cpUserId: this.verifiedCpUserId,
      cardTypeId: Number(this.payload.cardTypeId) || 0,
      cardMode: this.access.cardMode,
      subscriptionId: this.access.subscriptionId,
      rateId: this.access.rateId,
      gateGroupId: this.access.gateGroupId,
      clientId: this.clientId,
      issueDate: this.toDateInput(this.access.issueDate),
      expireDate: this.toDateInput(this.access.expireDate),
      isRenewal: this.mode === 'edit' && this.access.isRenewal,
      vehicles: (this.payload.vehicles as ParkingCardVehicleRow[]).map((v) => ({
        id: v.id ?? 0,
        licensePlate: v.licensePlate,
        provinceId: v.provinceId ?? 0,
        active: v.active ?? true,
        isDeleted: v.isDeleted ?? false,
        cityParkingVehicleId: v.cityParkingVehicleId ?? null,
        oldLicensePlate: v.id > 0 ? (v.oldLicensePlate ?? null) : null,
      })),
    };

    this.isSaving = true;

    if (this.isQuotaCreate) {
      const issuePayload = this.quotaMapper.buildIssuePayload(
        savePayload,
        menuId,
      );
      this.apiCardQuotaService
        .issuePlatformParkingCard(issuePayload)
        .subscribe({
          next: (res) => {
            console.log('Response Payload QuotaCreate : ', res);
            this.toast.success(
              `ออกบัตรสำเร็จ เหลือโควต้า ${res.quotaRemaining}`,
            );
            this.isSaving = false;
            this.onSaveSuccess();
          },
          error: (err) => {
            this.toast.error(err?.error?.message || 'ออกบัตรล้มเหลว');
            this.isSaving = false;
          },
        });
      return;
    }

    this.apiParkingCardService.saveParkingCardMember(savePayload).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          console.log('Response Payload Save: ', res);
          this.toast.success(res.message || 'บันทึกข้อมูลสำเร็จ');
          this.isSaving = false;
          this.onSaveSuccess();
        } else {
          this.toast.error(res.message || 'บันทึกข้อมูลไม่สำเร็จ');
          this.isSaving = false;
        }
      },
      error: (err) => {
        this.toast.error(
          err?.error?.message || err?.error?.Message || 'บันทึกข้อมูลไม่สำเร็จ',
        );
        this.isSaving = false;
      },
    });
  }

  validatePayload(): boolean {
    const steps: ('userInfo' | 'accessInfo' | 'vehicles')[] = [
      'userInfo',
      'accessInfo',
      'vehicles',
    ];
    for (const step of steps) {
      if (!this.validateStep(step)) {
        this.activeTab = step;
        return false;
      }
    }
    return true;
  }

  validateStep(step: 'userInfo' | 'accessInfo' | 'vehicles'): boolean {
    switch (step) {
      case 'userInfo':
        return this.validateUserStep();
      case 'accessInfo':
        return this.validateAccessStep();
      case 'vehicles':
        return this.validateVehicleStep();
    }
  }

  private validateUserStep(): boolean {
    if (!this.siteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar');
      return false;
    }
    if (!this.clientId) {
      return this.failField('client', 'กรุณาเลือกร้าน / Client');
    }
    if (this.isQuotaCreate) {
      if (this.isLoadingClientQuota) {
        this.toast.error('กำลังโหลดโควต้าร้าน กรุณารอสักครู่');
        return false;
      }
      if (!this.activeClientQuota) {
        this.toast.error('ไม่พบโควต้าสำหรับร้านนี้');
        this.focusField('client');
        return false;
      }
      if (this.activeClientQuota.quotaRemaining <= 0) {
        this.toast.error('โควต้าคงเหลือไม่เพียงพอ — ไม่สามารถออกบัตรเพิ่มได้');
        this.focusField('client');
        return false;
      }
    }
    if (!this.isFieldValid('line')) {
      return this.failField(
        'line',
        'กรุณาตรวจสอบบัญชี LINE ให้เรียบร้อย หรือเอาติ๊ก "มีบัญชี LINE" ออก',
      );
    }
    if (!this.isFieldValid('name')) {
      return this.failField('name', 'กรุณากรอกชื่อ-นามสกุล');
    }
    return true;
  }

  private validateAccessStep(): boolean {
    if (!this.isFieldValid('cardType')) {
      return this.isQuotaCreate
        ? this.failField('quotaType', 'กรุณาเลือกประเภทบัตร Platform')
        : this.failField('cardType', 'กรุณาเลือกประเภทสิทธิ์');
    }
    if (this.isQuotaCreate && !this.isFieldValid('quotaType')) {
      return this.failField(
        'quotaType',
        'ไม่พบประเภทบัตร/Subscription จากโควต้า — ตรวจสอบ whitelist (51) และโควต้าร้าน',
      );
    }
    if (!this.access.subscriptionId) {
      return this.failField('subscription', 'กรุณาเลือก Subscription');
    }
    if (!this.access.rateId || !this.access.gateGroupId) {
      return this.failField(
        'subscription',
        'Subscription ไม่มี rate / gate group — เลือก Subscription ใหม่',
      );
    }
    if (!this.access.issueDate) {
      return this.failField('issueDate', 'กรุณาระบุวันเริ่มสิทธิ์');
    }
    if (!this.access.expireDate) {
      return this.failField('expireDate', 'กรุณาระบุวันหมดอายุ');
    }
    return true;
  }

  private validateVehicleStep(): boolean {
    const vehicles = this.getActiveVehicles();
    if (vehicles.length === 0) {
      return this.failField('vehicles', 'ต้องมีทะเบียนรถอย่างน้อย 1 คัน');
    }
    if (vehicles.length > 1) {
      return this.failField(
        'vehicles',
        'ต้องเหลือทะเบียนรถเพียง 1 คัน — กรุณาลบทะเบียนส่วนเกินก่อนบันทึก',
      );
    }
    const rows = this.payload.vehicles as ParkingCardVehicleRow[];
    const badIndex = rows.findIndex(
      (v) => !v.isDeleted && (!v.licensePlate?.trim() || !v.provinceId),
    );
    if (badIndex >= 0) {
      const bad = rows[badIndex];
      return this.failField(
        'vehicleRows',
        'ทุกทะเบียนต้องระบุเลขทะเบียนและจังหวัด',
        bad.licensePlate?.trim() ? `province-${badIndex}` : `plate-${badIndex}`,
      );
    }
    return true;
  }

  /**
   * แปลงวันที่ (Date หรือ string) เป็นรูปแบบ YYYY-MM-DD เพื่อส่งให้ API
   */
  private toDateInput(value?: string | Date | null): string {
    if (!value) return '';
    if (typeof value === 'string') {
      const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match && value.length <= 10) return match[1];
    }
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // ================= Normal Functions =================

  /**
   * Prevents modal backdrop click handler from firing when clicking inside form elements.
   */
  onClickInsideModal(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Closes all custom select dropdowns and reverts typed text if it does not match a valid option.
   */
  closeDropdowns(): void {
    // 1. Card Type dropdown validation
    if (this.openDropdown === 'cardType') {
      const matched = this.cardTypes.find(
        (ct) =>
          ct.name.toLowerCase() ===
          (this.searchCardTypeText || '').trim().toLowerCase(),
      );
      if (matched) {
        this.payload.cardTypeId = String(matched.id);
        this.searchCardTypeText = matched.name;
      } else {
        const lastValid = this.cardTypes.find(
          (ct) => String(ct.id) === this.payload.cardTypeId,
        );
        this.searchCardTypeText = lastValid ? lastValid.name : '';
      }
    }
    // 2. Quota Type dropdown validation
    else if (this.openDropdown === 'quotaType') {
      const matched = this.platformCardTypes.find(
        (t) =>
          this.quotaTypeLabel(t).toLowerCase() ===
          (this.searchQuotaTypeText || '').trim().toLowerCase(),
      );
      if (matched) {
        this.quotaTypeKey = this.quotaTypeKeyOf(matched);
        this.searchQuotaTypeText = this.quotaTypeLabel(matched);
        this.onQuotaTypeChange();
      } else {
        const lastValid = this.platformCardTypes.find(
          (t) => this.quotaTypeKeyOf(t) === this.quotaTypeKey,
        );
        this.searchQuotaTypeText = lastValid
          ? this.quotaTypeLabel(lastValid)
          : '';
      }
    }
    // 3. Subscription dropdown validation
    else if (this.openDropdown === 'subscription') {
      const matched = this.subscriptions.find(
        (s) =>
          s.name.toLowerCase() ===
          (this.searchSubscriptionText || '').trim().toLowerCase(),
      );
      if (matched) {
        this.access.subscriptionId = matched.id;
        this.searchSubscriptionText = matched.name;
        this.onSubscriptionChange();
      } else {
        const lastValid = this.subscriptions.find(
          (s) => s.id === this.access.subscriptionId,
        );
        this.searchSubscriptionText = lastValid ? lastValid.name : '';
      }
    }
    // 4. Vehicle Province dropdown validation
    else if (
      this.openDropdown === 'vehicleProvince' &&
      this.openVehicleProvinceIndex !== null
    ) {
      const idx = this.openVehicleProvinceIndex;
      const v = this.payload.vehicles[idx];
      if (v) {
        const matched = this.provinces.find(
          (p) =>
            p.name.toLowerCase() ===
            (this.searchVehicleProvinceText || '').trim().toLowerCase(),
        );
        if (matched) {
          v.provinceId = matched.id;
          this.onProvinceChange(v);
        } else {
          const lastValid = this.provinces.find((p) => p.id === v.provinceId);
          this.searchVehicleProvinceText = lastValid ? lastValid.name : '';
        }
      }
      this.openVehicleProvinceIndex = null;
    }
    this.openDropdown = null;
  }

  /**
   * Sets focus state for custom select dropdown.
   */
  onInputFocus(menu: string): void {
    if (this.openDropdown && this.openDropdown !== menu) {
      this.closeDropdowns();
    }
    this.openDropdown = menu;
  }

  /**
   * Toggles custom select dropdown visibility.
   */
  toggleDropdown(menu: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdown === menu) {
      this.closeDropdowns();
    } else {
      if (this.openDropdown) this.closeDropdowns();
      this.openDropdown = menu;
    }
  }

  /**
   * Callback when card type is chosen.
   */
  selectCardType(ct: IdNameOption): void {
    this.payload.cardTypeId = String(ct.id);
    this.searchCardTypeText = ct.name;
    this.openDropdown = null;
  }

  /**
   * Callback when quota platform card type is chosen.
   */
  selectQuotaType(t: PlatformManagedCardType): void {
    this.quotaTypeKey = this.quotaTypeKeyOf(t);
    this.searchQuotaTypeText = this.quotaTypeLabel(t);
    this.openDropdown = null;
    this.onQuotaTypeChange();
  }

  /**
   * Callback when card mode status is chosen.
   */
  selectCardMode(val: number, label: string): void {
    this.access.cardMode = val;
    this.searchCardModeText = label;
    this.openDropdown = null;
  }

  /**
   * Callback when subscription option is chosen.
   */
  selectSubscription(s: IdNameOption): void {
    this.access.subscriptionId = s.id;
    this.searchSubscriptionText = s.name;
    this.openDropdown = null;
    this.onSubscriptionChange();
  }

  /**
   * Focuses and pre-fills province text field inside vehicles list row.
   */
  focusVehicleProvince(i: number, v: ParkingCardVehicleRow): void {
    this.openVehicleProvinceIndex = i;
    this.searchVehicleProvinceText =
      this.provinces.find((p) => p.id === v.provinceId)?.name || '';
    this.openDropdown = 'vehicleProvince';
  }

  /**
   * Callback when province is chosen for a vehicle row.
   */
  selectVehicleProvince(v: ParkingCardVehicleRow, p: ProvinceOption): void {
    v.provinceId = p.id;
    this.onProvinceChange(v);
    this.openDropdown = null;
    this.openVehicleProvinceIndex = null;
  }
}
